import React, { useState } from 'react';
import { X, Camera, FileText, Calendar, CheckSquare, Bell } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ShareWhatsappButton from './ShareWhatsappButton';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AddModal({ onClose, onSuccess }) {
  const [view, setView] = useState('menu');
  const [loading, setLoading] = useState(false);

  // Task form
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskPriority, setTaskPriority] = useState('medium');

  // Reminder form
  const [reminderTitle, setReminderTitle] = useState('');
  const [reminderDescription, setReminderDescription] = useState('');
  const [reminderDate, setReminderDate] = useState('');

  // Photo upload
  const [selectedFile, setSelectedFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [extractedData, setExtractedData] = useState(null);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setAnalyzing(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await axios.post(`${API}/api/documents/analyze`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setExtractedData(data);
      toast.success('¡Documento analizado correctamente!');
    } catch (error) {
      toast.error('Error al analizar el documento');
      console.error(error);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSaveDocument = async ({ createReminder = true } = {}) => {
    if (!selectedFile) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (extractedData) {
        formData.append('metadata_json', JSON.stringify(extractedData));
      }

      await axios.post(`${API}/api/documents/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Optionally create a reminder for the due date
      if (createReminder && extractedData?.due_date) {
        const remindAt = extractedData.event_time
          ? `${extractedData.due_date}T${extractedData.event_time}:00`
          : `${extractedData.due_date}T09:00:00`;
        const descriptionParts = [];
        if (extractedData.amount) {
          descriptionParts.push(
            `Monto: ${extractedData.currency || ''} ${extractedData.amount}`.trim()
          );
        }
        if (extractedData.assigned_to) descriptionParts.push(`Para: ${extractedData.assigned_to}`);
        if (extractedData.action_required) descriptionParts.push(extractedData.action_required);
        if (extractedData.items_to_bring) descriptionParts.push(`Llevar: ${extractedData.items_to_bring}`);
        await axios.post(
          `${API}/api/reminders`,
          {
            title: `${extractedData.service_name || 'Documento'}`,
            description: descriptionParts.join(' · ') || undefined,
            remind_at: remindAt,
            type: extractedData.document_type === 'factura' ? 'payment' : 'general',
          },
          { withCredentials: true }
        );
      }

      toast.success(
        createReminder && extractedData?.due_date
          ? 'Documento guardado y agregado al calendario'
          : 'Comprobante guardado'
      );
      onSuccess?.();
      onClose();
      // Defer refresh so the modal is fully unmounted before views reload,
      // avoiding a DOM race that occasionally raised removeChild errors.
      setTimeout(() => window.dispatchEvent(new CustomEvent('vida:data-changed')), 0);
    } catch (error) {
      toast.error('Error al guardar el documento');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAsExpense = async () => {
    if (!selectedFile || !extractedData) return;
    setLoading(true);
    try {
      // Save the underlying document too (with metadata) — keep its id to link the expense
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('metadata_json', JSON.stringify(extractedData));
      const uploadRes = await axios.post(`${API}/api/documents/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const documentId = uploadRes.data?.id || null;

      // Map IA document_type -> expense category
      const catMap = {
        factura: 'factura', recibo: 'recibo', comprobante: 'recibo',
        nota_escolar: 'colegio', presupuesto: 'otros', orden: 'otros',
      };
      const category = catMap[extractedData.document_type] || 'otros';
      const due = extractedData.due_date; // YYYY-MM-DD
      const period = due
        ? `${due.slice(0, 7)}`
        : (() => {
            const d = new Date();
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          })();

      await axios.post(
        `${API}/api/expenses`,
        {
          title: extractedData.service_name || 'Gasto',
          amount: Number(extractedData.amount) || 0,
          currency: extractedData.currency || 'ARS',
          category,
          period,
          due_date: due || null,
          paid_by: extractedData.assigned_to || null,
          shared_with: null,
          notes: extractedData.notes || extractedData.action_required || null,
          paid: false,
          document_id: documentId,
        },
        { withCredentials: true }
      );

      toast.success('Gasto creado desde la factura');
      onSuccess?.();
      onClose();
      setTimeout(() => window.dispatchEvent(new CustomEvent('vida:data-changed')), 0);
    } catch {
      toast.error('No pudimos crear el gasto');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    const title = (taskTitle || '').trim();
    if (title.length < 2) {
      toast.error('El título debe tener al menos 2 caracteres');
      return;
    }
    // Only send a due_date if the user entered a valid one; empty/invalid → null
    let dueDate = null;
    if (taskDueDate) {
      const d = new Date(taskDueDate);
      if (!isNaN(d.getTime())) dueDate = taskDueDate;
    }
    const validPriorities = ['low', 'medium', 'high'];
    const priority = validPriorities.includes(taskPriority) ? taskPriority : 'medium';

    setLoading(true);
    try {
      await axios.post(
        `${API}/api/tasks`,
        {
          title,
          description: (taskDescription || '').trim() || null,
          due_date: dueDate,
          priority,
        },
        { withCredentials: true }
      );

      toast.success('Tarea creada correctamente');
      onSuccess?.();
      onClose();
      setTimeout(() => window.dispatchEvent(new CustomEvent('vida:data-changed')), 0);
    } catch (error) {
      toast.error('Error al crear la tarea');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReminder = async (e) => {
    e.preventDefault();
    const title = (reminderTitle || '').trim();
    if (title.length < 2) {
      toast.error('El título es obligatorio');
      return;
    }
    if (!reminderDate) {
      toast.error('Elegí una fecha para el recordatorio');
      return;
    }
    const d = new Date(reminderDate);
    if (isNaN(d.getTime())) {
      toast.error('Fecha inválida');
      return;
    }
    setLoading(true);

    try {
      await axios.post(
        `${API}/api/reminders`,
        {
          title,
          description: (reminderDescription || '').trim() || null,
          remind_at: reminderDate,
          type: 'general',
        },
        { withCredentials: true }
      );

      toast.success('Recordatorio creado correctamente');
      onSuccess?.();
      onClose();
      setTimeout(() => window.dispatchEvent(new CustomEvent('vida:data-changed')), 0);
    } catch (error) {
      toast.error('Error al crear el recordatorio');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-surface rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-surface border-b border-black/5 p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-foreground" >
            {view === 'menu' && 'Agregar'}
            {view === 'task' && 'Nueva Tarea'}
            {view === 'reminder' && 'Nuevo Recordatorio'}
            {view === 'photo' && 'Subir Documento'}
          </h2>
          <button
            data-testid="close-modal-btn"
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-all"
          >
            <X className="w-6 h-6 text-muted-foreground" />
          </button>
        </div>

        <div className="p-6">
          {view === 'menu' && (
            <div className="space-y-3">
              <button
                data-testid="add-photo-btn"
                onClick={() => setView('photo')}
                className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-[#1E3A5F] to-[#2A4A6F] text-white rounded-2xl hover:shadow-lg transition-all hover:-translate-y-0.5"
              >
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Camera className="w-6 h-6" strokeWidth={1.5} />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold">Tomar una foto</h3>
                  <p className="text-sm text-white/80">Escanear factura o documento</p>
                </div>
              </button>

              <button
                data-testid="add-task-btn"
                onClick={() => setView('task')}
                className="w-full flex items-center gap-4 p-4 bg-surface border border-foreground/20 rounded-2xl hover:shadow-md transition-all hover:-translate-y-0.5"
              >
                <div className="w-12 h-12 bg-[#4CAF50]/10 rounded-full flex items-center justify-center">
                  <CheckSquare className="w-6 h-6 text-emerald-600 dark:text-emerald-400" strokeWidth={1.5} />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-foreground">Nueva tarea</h3>
                  <p className="text-sm text-muted-foreground">Crear una tarea pendiente</p>
                </div>
              </button>

              <button
                data-testid="add-reminder-btn"
                onClick={() => setView('reminder')}
                className="w-full flex items-center gap-4 p-4 bg-surface border border-foreground/20 rounded-2xl hover:shadow-md transition-all hover:-translate-y-0.5"
              >
                <div className="w-12 h-12 bg-amber-100 dark:bg-amber-500/15 rounded-full flex items-center justify-center">
                  <Bell className="w-6 h-6 text-amber-800 dark:text-amber-300" strokeWidth={1.5} />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-foreground">Nuevo recordatorio</h3>
                  <p className="text-sm text-muted-foreground">Configurar fecha y hora</p>
                </div>
              </button>
            </div>
          )}

          {view === 'task' && (
            <form onSubmit={handleCreateTask} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-muted-foreground mb-2">Título</label>
                <input
                  data-testid="task-title-input"
                  type="text"
                  value={taskTitle}
                  onChange={(e) => setTaskTitle(e.target.value)}
                  className="w-full px-4 py-3 bg-surface-2 border border-transparent rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent"
                  placeholder="Nombre de la tarea"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-muted-foreground mb-2">Descripción</label>
                <textarea
                  data-testid="task-description-input"
                  value={taskDescription}
                  onChange={(e) => setTaskDescription(e.target.value)}
                  className="w-full px-4 py-3 bg-surface-2 border border-transparent rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent"
                  placeholder="Detalles opcionales"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-muted-foreground mb-2">Fecha de vencimiento</label>
                <input
                  data-testid="task-due-date-input"
                  type="date"
                  value={taskDueDate}
                  onChange={(e) => setTaskDueDate(e.target.value)}
                  className="w-full px-4 py-3 bg-surface-2 border border-transparent rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-muted-foreground mb-2">Prioridad</label>
                <select
                  data-testid="task-priority-select"
                  value={taskPriority}
                  onChange={(e) => setTaskPriority(e.target.value)}
                  className="w-full px-4 py-3 bg-surface-2 border border-transparent rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent"
                >
                  <option value="low">Baja</option>
                  <option value="medium">Media</option>
                  <option value="high">Alta</option>
                </select>
              </div>

              <button
                data-testid="task-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#4CAF50] text-white rounded-full font-semibold hover:bg-[#45a049] transition-all hover:-translate-y-0.5 shadow-lg disabled:opacity-50"
              >
                {loading ? 'Creando...' : 'Crear Tarea'}
              </button>
            </form>
          )}

          {view === 'reminder' && (
            <form onSubmit={handleCreateReminder} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-muted-foreground mb-2">Título</label>
                <input
                  data-testid="reminder-title-input"
                  type="text"
                  value={reminderTitle}
                  onChange={(e) => setReminderTitle(e.target.value)}
                  className="w-full px-4 py-3 bg-surface-2 border border-transparent rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#FDE68A] focus:border-transparent"
                  placeholder="Nombre del recordatorio"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-muted-foreground mb-2">Descripción</label>
                <textarea
                  data-testid="reminder-description-input"
                  value={reminderDescription}
                  onChange={(e) => setReminderDescription(e.target.value)}
                  className="w-full px-4 py-3 bg-surface-2 border border-transparent rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#FDE68A] focus:border-transparent"
                  placeholder="Detalles opcionales"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-muted-foreground mb-2">Fecha y hora</label>
                <input
                  data-testid="reminder-date-input"
                  type="datetime-local"
                  value={reminderDate}
                  onChange={(e) => setReminderDate(e.target.value)}
                  className="w-full px-4 py-3 bg-surface-2 border border-transparent rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#FDE68A] focus:border-transparent"
                  required
                />
              </div>

              <button
                data-testid="reminder-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#D97706] text-white rounded-full font-semibold hover:bg-[#B45309] transition-all hover:-translate-y-0.5 shadow-lg disabled:opacity-50"
              >
                {loading ? 'Creando...' : 'Crear Recordatorio'}
              </button>
            </form>
          )}

          {view === 'photo' && (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-foreground/30 rounded-2xl p-8 text-center hover:border-foreground transition-all">
                <input
                  data-testid="photo-upload-input"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="photo-upload"
                />
                <label htmlFor="photo-upload" className="cursor-pointer block">
                  <Camera className="w-12 h-12 text-foreground mx-auto mb-3" strokeWidth={1.5} />
                  <p className="text-lg font-semibold text-foreground mb-1">Seleccionar imagen</p>
                  <p className="text-sm text-muted-foreground">Sube una foto de tu factura o documento</p>
                </label>
              </div>

              {analyzing && (
                <div className="text-center py-4">
                  <div className="w-12 h-12 border-4 border-foreground border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                  <p className="text-muted-foreground">Analizando documento...</p>
                </div>
              )}

              {extractedData && (
                <div className="bg-emerald-100 dark:bg-emerald-500/15 border border-[#4CAF50]/20 rounded-2xl p-6 space-y-3">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-foreground">¡Datos extraídos!</h3>
                    {extractedData.document_type && (
                      <span
                        data-testid="doc-type-badge"
                        className="px-3 py-1 text-xs font-semibold rounded-full bg-[#1E3A5F] text-white capitalize"
                      >
                        {String(extractedData.document_type).replace('_', ' ')}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {extractedData.service_name && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Servicio / Evento</p>
                        <p className="font-semibold text-foreground" data-testid="doc-service-name">
                          {extractedData.service_name}
                        </p>
                      </div>
                    )}
                    {(extractedData.amount || extractedData.amount === 0) && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Monto</p>
                        <p className="font-semibold text-foreground" data-testid="doc-amount">
                          {extractedData.currency || ''} {extractedData.amount}
                        </p>
                      </div>
                    )}
                    {extractedData.due_date && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Fecha</p>
                        <p className="font-semibold text-foreground" data-testid="doc-due-date">
                          {extractedData.due_date}
                          {extractedData.event_time ? ` · ${extractedData.event_time}` : ''}
                        </p>
                      </div>
                    )}
                    {extractedData.assigned_to && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">A quién corresponde</p>
                        <p className="font-semibold text-foreground" data-testid="doc-assigned-to">
                          {extractedData.assigned_to}
                        </p>
                      </div>
                    )}
                  </div>

                  {extractedData.action_required && (
                    <div className="pt-2 border-t border-[#4CAF50]/30">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Qué hacer</p>
                      <p className="text-sm text-foreground" data-testid="doc-action">
                        {extractedData.action_required}
                      </p>
                    </div>
                  )}

                  {extractedData.items_to_bring && (
                    <div className="pt-2 border-t border-[#4CAF50]/30">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Qué llevar</p>
                      <p className="text-sm text-foreground" data-testid="doc-items">
                        {extractedData.items_to_bring}
                      </p>
                    </div>
                  )}

                  {extractedData.notes && (
                    <div className="pt-2 border-t border-[#4CAF50]/30">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Notas</p>
                      <p className="text-sm text-muted-foreground">{extractedData.notes}</p>
                    </div>
                  )}

                  <div className="space-y-2 pt-2">
                    {extractedData.due_date && (
                      <button
                        data-testid="save-document-btn"
                        onClick={() => handleSaveDocument({ createReminder: true })}
                        disabled={loading}
                        className="w-full py-3 bg-[#4CAF50] text-white rounded-full font-semibold hover:bg-[#45a049] transition-all hover:-translate-y-0.5 shadow-lg disabled:opacity-50"
                      >
                        {loading ? 'Guardando...' : 'Guardar y agregar al calendario'}
                      </button>
                    )}
                    <button
                      data-testid="save-receipt-btn"
                      onClick={() => handleSaveDocument({ createReminder: false })}
                      disabled={loading}
                      className="w-full py-3 bg-surface border-2 border-foreground text-foreground rounded-full font-semibold hover:bg-[#1E3A5F] hover:text-white transition-all disabled:opacity-50"
                    >
                      {loading ? 'Guardando...' : 'Guardar solo como comprobante'}
                    </button>
                    {(extractedData.amount || extractedData.amount === 0) &&
                      ['factura', 'recibo', 'comprobante', 'nota_escolar', 'presupuesto', 'orden'].includes(extractedData.document_type) && (
                        <button
                          data-testid="save-as-expense-btn"
                          onClick={handleSaveAsExpense}
                          disabled={loading}
                          className="w-full py-3 bg-[#1E3A5F] text-white rounded-full font-semibold hover:bg-[#152B47] transition-all shadow-md hover:-translate-y-0.5 disabled:opacity-50"
                        >
                          {loading ? 'Guardando...' : '💰 Guardar como Gasto'}
                        </button>
                      )}
                    <ShareWhatsappButton
                      title={extractedData.service_name || 'Documento'}
                      date={extractedData.due_date}
                      description={[
                        extractedData.assigned_to && `Para: ${extractedData.assigned_to}`,
                        extractedData.action_required,
                        extractedData.items_to_bring && `Llevar: ${extractedData.items_to_bring}`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                      amount={extractedData.amount}
                      currency={extractedData.currency}
                      kind={extractedData.document_type === 'factura' ? 'bill' : 'reminder'}
                      withLabel
                      variant="solid"
                      testId="share-document-btn"
                      className="w-full justify-center"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}